// src/Games/GamesHooks/BimaPathData.js

export const PREMIUM_TABLES = {
  term: {
    22: 280, 25: 320, 28: 380, 30: 420, 32: 470, 35: 580,
    38: 720, 40: 850, 42: 1000, 45: 1280, 48: 1620, 50: 1950,
    55: 2900, 60: 4500, 65: 7000,
  },
  whole: {
    22: 1100, 25: 1280, 28: 1460, 30: 1600, 32: 1780, 35: 2050,
    38: 2350, 40: 2580, 42: 2820, 45: 3200, 48: 3650, 50: 4100,
    55: 5300, 60: 6800, 65: 8800,
  },
  endow: {
    22: 3400, 25: 3520, 28: 3680, 30: 3800, 32: 3940, 35: 4180,
    38: 4450, 40: 4650, 42: 4870, 45: 5220, 48: 5630, 50: 6080,
    55: 7100, 60: 8400, 65: 9900,
  },
};

export const LIFE_EVENT_OFFSETS = [3, 8, 13, 18, 23];

export const WALKTHROUGH_CARDS = [
  {
    icon: '🛡️',
    title: 'What is life insurance?',
    body: `A contract with an insurer. You pay them a small amount each month (called a <strong>premium</strong>). If you die while the contract is active, they pay your family a much larger amount (called the <strong>sum assured</strong>) — usually a few million shillings.`,
    sub: "That's it. The complications are all in the details.",
  },
  {
    icon: '🌳',
    title: 'Three main types',
    body: `<strong>Term life</strong> — cheap, covers you for a set period (usually 20 years). Pays only if you die during that period.<br><br><strong>Whole life</strong> — 3-4× more expensive, but covers you for life and builds a savings amount (<strong>cash value</strong>) you can borrow against.<br><br><strong>Endowment</strong> — most expensive. A savings product wearing an insurance jacket. Pays at the end of the term or when you die, whichever comes first.`,
    sub: 'The agent in the game will try to sell you the expensive one. Watch carefully.',
  },
  {
    icon: '⚠️',
    title: 'Two things you must do',
    body: `<strong>1. Tell the truth on the application.</strong> If you hide a medical condition or a risky job, your family's claim can be voided after you die. They'll find out — insurers investigate.<br><br><strong>2. Read the policy document.</strong> It lists what's NOT covered (exclusions). Suicide in the first 2 years, drug-related deaths, certain hazardous activities — different policies exclude different things.`,
    sub: 'Most people skip both. The game shows what happens then.',
  },
  {
    icon: '⚖️',
    title: "If you're treated unfairly",
    body: `When an insurer denies a claim, that's often not the final word. Kenya has the <strong>Insurance Regulatory Authority (IRA)</strong> — a government body that handles consumer complaints for free. Email: complaints@ira.go.ke.<br><br>They actually rule against insurers when the case has merit. Most policyholders don't know this exists.`,
    sub: 'The game shows you how to use it.',
  },
  {
    icon: '▶️',
    title: 'Ready to play?',
    body: `You'll pick a character to follow, then walk through 25 years of their life. Some decisions look great today and ruin them in 10 years. Some look painful but save them when it matters.<br><br>There are no wrong choices — only consequences.`,
    sub: 'About 8 minutes. Tap through the events as they come.',
  },
];

export const PERSONAS = {
  wanjiru: {
    name: 'Wanjiru',
    headline: '35 · Married · 2 kids · Marketing manager',
    blurb:
      "Wanjiru lives in Westlands with her husband Brian (a software engineer) and their two children, aged 5 and 2. She earns KSh 130,000 a month at a media agency. Her parents are getting older and starting to depend on her. She's never had life insurance.",
    state: {
      age: 35,
      maritalStatus: 'married',
      children: 2,
      dependants: 'parents',
      occupation: 'office',
      income: 130000,
      spouseIncome: 80000,
      health: 'clean',
    },
  },
  kamau: {
    name: 'Kamau',
    headline: '28 · Single · No dependants · Software developer',
    blurb:
      "Kamau lives in Kilimani, rides a bodaboda to work some days when traffic is bad, and earns KSh 70,000 at a mid-size fintech. No spouse, no kids, parents still working. He's heard friends talk about insurance but doesn't really get it.",
    state: {
      age: 28,
      maritalStatus: 'single',
      children: 0,
      dependants: 'none',
      occupation: 'driver_rider',
      income: 70000,
      spouseIncome: 0,
      health: 'clean',
    },
  },
  mzee: {
    name: 'Mzee Otieno',
    headline: '52 · Married · Kids grown · Business owner',
    blurb:
      'Mzee Otieno runs a hardware shop in Ngong. His three children are grown and out of the house, but two siblings depend on him and his wife Mama Janet helps him at the shop. He earns roughly KSh 220,000 a month. He has mild hypertension, controlled with medication.',
    state: {
      age: 52,
      maritalStatus: 'married',
      children: 0,
      dependants: 'parents_siblings',
      occupation: 'self_employed',
      income: 220000,
      spouseIncome: 40000,
      health: 'minor',
    },
  },
};

export const INCOME_TIERS = [
  { v: 35000, label: 'KSh 30K–45K', desc: 'Entry-level, junior staff' },
  { v: 70000, label: 'KSh 60K–85K', desc: 'Mid-level, established' },
  { v: 130000, label: 'KSh 100K–150K', desc: 'Senior, specialist' },
  { v: 220000, label: 'KSh 180K–250K', desc: 'Management, consultant' },
  { v: 400000, label: 'KSh 350K+', desc: 'Executive, senior partner' },
];

// ============================================================
// HELPERS
// ============================================================
export function fmt(n) {
  return Math.round(n).toLocaleString('en-KE');
}

export function premiumPer1M(age, type) {
  const table = PREMIUM_TABLES[type];
  if (!table) return 420;
  const ages = Object.keys(table).map(Number).sort((a, b) => a - b);
  if (age <= ages[0]) return table[ages[0]];
  if (age >= ages[ages.length - 1]) return table[ages[ages.length - 1]];
  for (let i = 0; i < ages.length - 1; i++) {
    if (age >= ages[i] && age <= ages[i + 1]) {
      const frac = (age - ages[i]) / (ages[i + 1] - ages[i]);
      return Math.round(table[ages[i]] + frac * (table[ages[i + 1]] - table[ages[i]]));
    }
  }
  return table[30];
}

export function calculatePremium(state, type, sumAssured) {
  let base = premiumPer1M(state.age, type) * (sumAssured / 1000000);
  if (state.health === 'minor' || state.health === 'chronic') {
    if (state.discCondition === 'honest') base *= state.health === 'chronic' ? 1.6 : 1.25;
  }
  if (state.occupation === 'hazardous' || state.occupation === 'driver_rider') {
    if (state.discHazard === 'honest') base *= 1.35;
  }
  if (state.riderCritical) base += (sumAssured / 1000000) * 380;
  if (state.riderDisability) base += (sumAssured / 1000000) * 220;
  return Math.round(base);
}

export function calculateSurrenderValue(state, yearsInForce, type) {
  const projectedPremiums = state.monthlyPremium * 12 * yearsInForce;
  if (yearsInForce < 2) return 0;
  if (type === 'term' || type === 'term_invest') return 0;
  if (type === 'whole') {
    if (yearsInForce < 5) return Math.round(projectedPremiums * 0.2);
    if (yearsInForce < 10) return Math.round(projectedPremiums * 0.45);
    if (yearsInForce < 15) return Math.round(projectedPremiums * 0.6);
    if (yearsInForce < 20) return Math.round(projectedPremiums * 0.72);
    return Math.round(projectedPremiums * 0.85);
  }
  if (type === 'endowment') {
    if (yearsInForce < 5) return Math.round(projectedPremiums * 0.25);
    if (yearsInForce < 10) return Math.round(projectedPremiums * 0.55);
    if (yearsInForce < 15) return Math.round(projectedPremiums * 0.75);
    if (yearsInForce < 20) return Math.round(projectedPremiums * 0.95);
    return Math.round(projectedPremiums * 1.15);
  }
  return 0;
}

// ============================================================
// CHOICE FUNCTION FACTORIES
// ============================================================

export function incomeShockChoices(s) {
  const surrValue = calculateSurrenderValue(s, 3, s.product);
  const choices = [
    {
      label: 'Pay the premium, eat into savings',
      meta: 'Cover stays active. Painful but disciplined.',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 3;
        accrueMMFDirect(s, 3);
        return {
          story:
            'You keep paying. You burn through your buffer. By year 4 your income recovers. The policy is fully in force. The lesson: liquid emergency savings are how you keep insurance from becoming the first thing you drop in a crisis.',
          tone: 'good',
        };
      },
    },
    {
      label: 'Skip 3 months, hope to restart later',
      meta: 'Policy will lapse after the grace period.',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 2;
        accrueMMFDirect(s, 2);
        s.policyLapsed = true;
        s.lapsedAtYear = 3;
        return {
          story:
            "You stop the auto-debit for three months. The insurer sends two reminders. Day 91, the policy lapses. By the time your income recovers, you try to reinstate — but they require a fresh medical exam. The original underwriting terms are gone.",
          tone: 'bad',
          lapsed: true,
        };
      },
    },
  ];
  if (surrValue > 0) {
    choices.push({
      label: `Surrender the policy — take ${fmt(surrValue)} cash`,
      meta: `Get ~${Math.round((surrValue / (s.monthlyPremium * 12 * 3)) * 100)}% of premiums back. Lose the rest.`,
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 3;
        accrueMMFDirect(s, 3);
        s.surrenderedAmount = surrValue;
        s.surrenderedAtYear = 3;
        s.policyLapsed = true;
        s.lapsedAtYear = 3;
        return {
          story: `You surrender. The cash surrender value at year 3 is KSh ${fmt(surrValue)} — only ~${Math.round((surrValue / s.totalPremiumsPaid) * 100)}% of what you've paid in. The rest is gone. You now have no cover. <strong>Early surrender is one of the worst financial decisions in insurance — the loadings are heaviest in early years and surrender values are correspondingly low.</strong>`,
          tone: 'bad',
          lapsed: true,
        };
      },
    });
  }
  choices.push({
    label: 'Borrow against the policy (if whole life)',
    meta:
      s.product === 'whole' ? 'Available at year 3+' : 'Not available for your product',
    apply: (s) => {
      s.totalPremiumsPaid += s.monthlyPremium * 12 * 3;
      accrueMMFDirect(s, 3);
      if (s.product === 'whole') {
        const loanAmt = Math.round(s.totalPremiumsPaid * 0.6);
        return {
          story: `You take a policy loan of KSh ${fmt(loanAmt)} against your whole-life cash value at ~12% interest. Premium continues, cover stays in force. You pay the loan back over the following years. This is one of whole life's genuine advantages — you don't have to surrender to access cash. The catch: if you die before repaying, the loan plus interest is deducted from the payout.`,
          tone: 'neutral',
        };
      }
      return {
        story:
          "Your product (term/endowment) doesn't have a borrowable cash value at year 3. You take a personal bank loan instead at 15%. Premium stays paid. Insurance stays in force.",
        tone: 'neutral',
      };
    },
  });
  return choices;
}

export function hazardCloseCallChoices(s) {
  return [
    {
      label:
        s.discHazard === 'hide'
          ? 'Stay quiet and hope it never comes up'
          : 'Take stock of your cover; consider topping up',
      meta: 'Standard adult response',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        let storyText = 'You think about it for a week, then life continues. No changes made.';
        if (s.discHazard === 'hide') {
          storyText = `<strong>Critical educational moment.</strong> You didn't disclose the hazardous activity at application. If this incident had killed you, the insurer's investigators would have pulled phone records, talked to colleagues, and reconstructed your activity pattern. <strong>Your family's claim would have been rejected for material non-disclosure under Section 153 of the Insurance Act.</strong> The premiums you paid would be refunded but the ${fmt(s.sumAssured)} would not. You got lucky this time.`;
        }
        return { story: storyText, tone: s.discHazard === 'hide' ? 'alert' : 'good' };
      },
    },
    {
      label: 'Top up your cover by 3M sum assured',
      meta:
        'Additional premium ~KSh ' +
        fmt(premiumPer1M(s.age + 8, s.product === 'term_invest' ? 'term' : s.product) * 3),
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        const topUp =
          premiumPer1M(s.age + 8, s.product === 'term_invest' ? 'term' : s.product) * 3;
        s.monthlyPremium += topUp;
        s.sumAssured += 3000000;
        return {
          story: `You top up. New sum assured: KSh ${fmt(s.sumAssured)}. New premium: KSh ${fmt(s.monthlyPremium)}/mo. The top-up triggered a new medical exam — you're still healthy enough that nothing was loaded.`,
          tone: 'good',
        };
      },
    },
  ];
}

export function parentHospitalChoices(s) {
  return [
    {
      label: 'Pay the full 180K bill from savings',
      meta: 'Drain emergency buffer, cover stays',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You send KSh 180K for the parent's hospital bill. Savings are now almost empty. But your insurance stays in force. The premium suddenly feels significant — but skipping it now would mean re-applying at a higher age with possibly worse underwriting.",
          tone: 'good',
        };
      },
    },
    {
      label: 'Pause premiums for 6 months to fund the medical bill',
      meta: 'Policy at risk of lapse — saves on premium during crisis',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        s.policyLapsed = true;
        s.lapsedAtYear = 8;
        return {
          story:
            "You stop premiums and redirect to the 180K medical bill. The 3-month grace period passes, then a 30-day reinstatement window. You miss both. Policy is now lapsed. <strong>Your parents got help; you became uninsured at the moment your own dependants are growing into adulthood.</strong>",
          tone: 'bad',
          lapsed: true,
        };
      },
    },
    {
      label: 'Take a SACCO loan to cover the 180K bill',
      meta: '~12% interest over 18 months. Insurance preserved.',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            'SACCO loan of KSh 200K at 12% over 18 months. Monthly repayment of ~12.5K is added to your obligations. Premium stays paid. Both your parent and your insurance are protected. This is a textbook use of cheaper debt to preserve protective infrastructure.',
          tone: 'good',
        };
      },
    },
    {
      label: 'Pay 180K now AND enroll parents in medical cover going forward',
      meta: '180K bill + ~10K/mo new commitment',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You pay the 180K bill from savings. Then you enroll your parents in a Britam/Jubilee/Old Mutual family floater medical cover at ~10K/month going forward. Future hospital scares won't hit your savings the same way. The 10K monthly is now permanent but it's far less than another 180K shock. <strong>This is the smart play — handle today's crisis AND prevent it from recurring.</strong>",
          tone: 'good',
        };
      },
    },
  ];
}

export function kidMedicalChoices(s) {
  return [
    {
      label: 'Pay the 240K from savings, keep premium going',
      meta: 'Drain emergency buffer. Cover stays.',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "Your child is fine. Savings took a 240K hit. The life cover continues. This is exactly the kind of event medical insurance is designed for — but you don't have any for the kids. <strong>The bill is paid; the lesson is filed away.</strong>",
          tone: 'neutral',
        };
      },
    },
    {
      label: 'Pay the 240K AND enroll the kids in medical cover going forward',
      meta: `240K bill + ~${s.children * 4000}/mo new commitment`,
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story: `You pay the 240K bill from savings. Then you enroll the children in a child medical scheme at ~${fmt(s.children * 4000)}/month going forward. This is the lesson many Kenyan parents learn at 240K. Now you've learned it once and converted it into ongoing protection. <strong>The smart play — handle today's crisis AND prevent it from recurring.</strong>`,
          tone: 'good',
        };
      },
    },
    {
      label: 'Sell shares / cash out part of the SACCO to cover the bill',
      meta: 'Liquidate assets, avoid new debt',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            'You cash out 240K from your SACCO deposits. The savings infrastructure you built took years to assemble; one weekend rebuilds half of it. The child is fine. No new debt taken.',
          tone: 'neutral',
        };
      },
    },
    {
      label: 'SACCO loan to cover the 240K bill',
      meta: '~12% interest over 18 months. Preserves savings.',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "SACCO loan of 240K at 12% over 18 months. Monthly repayment ~14.5K added to your obligations. Your savings stay intact. Cover stays in force. The child is fine. You're absorbing a 240K shock through cheap debt rather than savings depletion.",
          tone: 'good',
        };
      },
    },
  ];
}

export function careerPivotChoices(s) {
  return [
    {
      label: 'Notify the insurer, set up forex premium payment',
      meta: 'Cover preserved through the move',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You write to the insurer, get the policy noted as 'expatriate', arrange premium payment via a Kenyan bank standing order. The 2 years in Dubai go fine. Cover continued throughout. You return with much more savings AND your insurance intact.",
          tone: 'good',
        };
      },
    },
    {
      label: "Just leave — sort the insurance later",
      meta: 'Easy to forget premiums when abroad',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        if (Math.random() < 0.6) {
          s.policyLapsed = true;
          s.lapsedAtYear = 8;
          return {
            story:
              "Four months in, you realize the auto-debit failed because your Kenyan bank flagged the foreign-origin standing order. By the time you sort it, the policy has lapsed. You're now uninsured. Reinstatement requires a fresh medical exam and possibly higher rates.",
            tone: 'bad',
            lapsed: true,
          };
        }
        return {
          story:
            "You got away with it. The premiums kept flowing through some miracle of bank automation. You return to Kenya 2 years later with the policy still in force. Lucky.",
          tone: 'neutral',
        };
      },
    },
    {
      label: "Surrender the policy, restart when you're back",
      meta: "Famous mistake — premiums get harder with age",
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        const surr = calculateSurrenderValue(s, 8, s.product);
        if (surr > 0) {
          s.surrenderedAmount = surr;
          s.surrenderedAtYear = 8;
        }
        s.policyLapsed = true;
        s.lapsedAtYear = 8;
        return {
          story: `You surrender. ${surr > 0 ? `Cash recovered: KSh ${fmt(surr)}.` : 'No surrender value on term life — you just stop paying.'} When you try to take out a new policy in Dubai or back in Kenya, the premium for the same cover is significantly higher because you're older. The "restart" costs more than continuing would have.`,
          tone: 'bad',
          lapsed: true,
        };
      },
    },
  ];
}

export function friendDeathChoices(s) {
  return [
    {
      label: 'Top up your cover by 3M sum assured',
      meta: 'Premium goes up but family is better protected',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        const topUp =
          premiumPer1M(s.age + 8, s.product === 'term_invest' ? 'term' : s.product) * 3;
        s.monthlyPremium += topUp;
        s.sumAssured += 3000000;
        return {
          story: `You request a top-up. New medical exam. Your sum assured rises to KSh ${fmt(s.sumAssured)}. Premium now KSh ${fmt(s.monthlyPremium)}/mo. Your family is now properly protected.`,
          tone: 'good',
        };
      },
    },
    {
      label: s.riderCritical
        ? 'Add Critical Illness rider if not already'
        : 'Add Critical Illness rider',
      meta: s.riderCritical ? 'Already have it' : 'Lump sum on diagnosis',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        if (s.riderCritical) {
          return {
            story:
              "You already have CI rider — confirmed. The friend's death made you check the policy. Good move.",
            tone: 'good',
          };
        }
        s.riderCritical = true;
        const extra = Math.round((s.sumAssured / 1000000) * 380);
        s.monthlyPremium += extra;
        return {
          story: `CI rider added. Extra KSh ${fmt(extra)}/mo. 6-month waiting period before claims can be made (this is standard). You've closed a significant gap in your protection.`,
          tone: 'good',
        };
      },
    },
    {
      label: 'Think about it, do nothing',
      meta: 'Standard response',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You think about it for a week. Life continues. No changes made. The cover stays the same. This is what 80% of people do after a friend's death.",
          tone: 'neutral',
        };
      },
    },
  ];
}

export function medicalRecordsChoices(s) {
  return [
    {
      label: 'Proactively contact the insurer and amend the disclosure',
      meta: 'Honest, painful, correct',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        s.discCondition = 'honest';
        s.monthlyPremium = Math.round(s.monthlyPremium * 1.4);
        return {
          story:
            "You write to the insurer. They review. The policy was issued on the basis of misrepresentation. They give you two options: (1) void the policy and refund premiums, or (2) issue a corrected policy backdated, with a higher premium and a 2-year waiting period for any condition-related claim. You take option 2. <strong>This is rare in real life — most people only fix this when forced to. Doing it now saves your family millions later.</strong>",
          tone: 'good',
        };
      },
    },
    {
      label: 'Decline the medical records request',
      meta: 'Triggers underwriting review',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You decline. The insurer notes this on file. Future claims will trigger more aggressive investigation. You bought time but didn't solve anything.",
          tone: 'bad',
        };
      },
    },
    {
      label: "Comply with the request, hope it doesn't come back",
      meta: 'Gambling',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You consent. Records pulled. The insurer notes the pre-existing condition but doesn't void the policy (yet) — they'll use it as grounds for denial if you ever claim related to that condition. <strong>Your policy is essentially neutered for that condition.</strong> You don't know this until claim time.",
          tone: 'bad',
        };
      },
    },
  ];
}

export function familyPlanningChoices(s) {
  return [
    {
      label: 'Add a spouse rider — both lives covered',
      meta: 'Premium ~+40-60% for joint cover',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        s.monthlyPremium = Math.round(s.monthlyPremium * 1.5);
        return {
          story:
            'Spouse rider added. New medical exam for spouse. Both lives covered under one policy now. Premium up 50%. Peace of mind: total.',
          tone: 'good',
        };
      },
    },
    {
      label: 'Get spouse their own separate policy',
      meta: 'Two policies, more flexibility',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "Spouse takes out their own term policy at their age. Lower combined cost than a rider in some cases. Two separate policies = two separate negotiation positions if anything goes wrong with one insurer.",
          tone: 'good',
        };
      },
    },
    {
      label: 'Defer — figure it out after the baby arrives',
      meta: 'Common, suboptimal',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You decide to deal with it later. Spouse remains uninsured. Most people do this. The cost is invisible until something happens.",
          tone: 'neutral',
        };
      },
    },
  ];
}

export function schoolFeesCrunchChoices(s) {
  const surrValue = calculateSurrenderValue(s, 13, s.product);
  const choices = [
    {
      label: 'Pay fees from SACCO loan (320K), keep premium going',
      meta: 'Take on debt, preserve cover',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            'SACCO loan of 320K at 12% over 24 months. Adds 15K monthly to your obligations. Insurance stays in force. Kids in school. Stressful but workable — and cheaper than the alternatives.',
          tone: 'good',
        };
      },
    },
    {
      label: 'Pay 320K from savings, reduce sum assured to ease future premiums',
      meta: 'Liquidate buffer + lower ongoing cost',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        const oldSA = s.sumAssured;
        const oldPremium = s.monthlyPremium;
        s.sumAssured = Math.round(s.sumAssured * 0.6);
        s.monthlyPremium = Math.round(s.monthlyPremium * 0.6);
        return {
          story: `You pay the 320K from savings, which empties most of your buffer. To ease the burden going forward, you call the insurer and reduce sum assured from KSh ${fmt(oldSA)} to KSh ${fmt(s.sumAssured)}. Premium drops from KSh ${fmt(oldPremium)} to KSh ${fmt(s.monthlyPremium)} per month. You stay covered but at a lower level. Pragmatic middle path.`,
          tone: 'neutral',
        };
      },
    },
  ];
  if (surrValue > 0) {
    choices.push({
      label: `Surrender the policy — get ${fmt(surrValue)}, use for fees`,
      meta: `Recover ~${Math.round((surrValue / (s.monthlyPremium * 12 * 13)) * 100)}% of premiums paid. End cover.`,
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        s.surrenderedAmount = surrValue;
        s.surrenderedAtYear = 13;
        s.policyLapsed = true;
        s.lapsedAtYear = 13;
        const balance = 320000 - surrValue;
        return {
          story: `You surrender. Cash value at year 13: KSh ${fmt(surrValue)}. You use it toward the 320K fees${balance > 0 ? ` and find the remaining KSh ${fmt(balance)} from savings` : ' with some left over'}. Kids' fees are paid. You're now uninsured at age ${s.age + 13}. Re-applying for new cover at this age will cost significantly more. <strong>This is one of the most common pivots in real Kenyan policyholder behavior — and one of the most regretted.</strong>`,
          tone: 'bad',
          lapsed: true,
        };
      },
    });
  }
  choices.push({
    label: 'Skip premium this term + drain savings to pay fees',
    meta: 'Saves ~24K in premium, rest from buffer. Cover at risk.',
    apply: (s) => {
      s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
      accrueMMFDirect(s, 5);
      s.policyLapsed = true;
      s.lapsedAtYear = 13;
      return {
        story:
          "You stop premium for the term (saving ~24K over 3 months) and drain savings for the 296K balance. Policy lapses after the 90-day grace period. Fees are paid. You're uninsured. The reinstatement window passes while you're focused on the kids. By the time you check, you need a new application — at age 43, with potentially worse terms.",
        tone: 'bad',
        lapsed: true,
      };
    },
  });
  return choices;
}

export function ownHealthDiagnosisChoices(s) {
  return [
    {
      label: 'Notify the insurer about the diagnosis',
      meta: 'Honest, may affect future riders',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            'You notify them. They note it on your file. Future critical illness rider claims related to this condition will have a 2-year waiting period. The policy continues. Honesty preserved.',
          tone: 'good',
        };
      },
    },
    {
      label: "Say nothing — it's not material",
      meta: 'Common move',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        return {
          story:
            "You don't inform the insurer. New conditions diagnosed AFTER your policy started don't typically require notification under Kenyan policies. But if you ever add a rider or change cover, they'll come up in the new medical exam.",
          tone: 'neutral',
        };
      },
    },
    s.riderCritical
      ? {
          label: 'Check what your existing CI rider covers',
          meta: 'You already have one',
          apply: (s) => {
            s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
            accrueMMFDirect(s, 5);
            return {
              story:
                'You read the CI rider schedule carefully. The covered conditions are typically: cancer, heart attack, stroke, kidney failure, major organ transplant, multiple sclerosis. Hypertension itself is NOT a CI condition — but a stroke caused by it WOULD be. Future strokes related to existing hypertension have a 2-year waiting period from this diagnosis date. Good to know now.',
              tone: 'good',
            };
          },
        }
      : {
          label: 'Try to add a Critical Illness rider now',
          meta: 'Reactive — premium will be loaded',
          apply: (s) => {
            s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
            accrueMMFDirect(s, 5);
            s.riderCritical = true;
            const extra = Math.round((s.sumAssured / 1000000) * 380 * 1.45);
            s.monthlyPremium += extra;
            return {
              story: `The insurer accepts the rider but adds 45% loading because of the new diagnosis. The CI rider now costs you KSh ${fmt(extra)}/mo extra. Conditions related to the new diagnosis will have a 2-year waiting period before claims can be made. <strong>Lesson: riders are cheapest to add at application, not when you need them.</strong>`,
              tone: 'alert',
            };
          },
        },
  ];
}

export function claimEventChoices(s) {
  if (s.policyLapsed) {
    const wasSurrendered = s.surrenderedAmount > 0;
    return [
      {
        label: 'Pay from savings + family contributions',
        meta: 'Drain everything you have',
        apply: (s) => {
          s.claimEventOccurred = true;
          s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
          accrueMMFDirect(s, 5);
          s.claimOutcome = wasSurrendered ? 'no_cover_surrendered' : 'no_cover_lapsed';
          s.claimAmount = 0;
          return {
            story: wasSurrendered
              ? `<strong>No cover — you surrendered the policy at year ${s.surrenderedAtYear}.</strong> The KSh ${fmt(s.surrenderedAmount)} you recovered then is now part of your savings. You drain what's left plus contributions from family. Treatment begins but the financial stress is real. The protection you cancelled would have paid for this exact event.`
              : `<strong>No cover — policy lapsed at year ${s.lapsedAtYear}.</strong> You drain savings plus contributions from family to fund treatment. No insurer to call. No claim to file. <strong>This is the moment lapsed policies become real — when the event the policy was designed for finally happens and there's no policy left.</strong>`,
            tone: 'bad',
          };
        },
      },
      {
        label: 'Crowdfund / use M-Changa / family WhatsApp group',
        meta: 'Public ask, shared burden',
        apply: (s) => {
          s.claimEventOccurred = true;
          s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
          accrueMMFDirect(s, 5);
          s.claimOutcome = wasSurrendered ? 'no_cover_surrendered' : 'no_cover_lapsed';
          s.claimAmount = 0;
          return {
            story:
              'You start a fundraiser. Family WhatsApp group, M-Changa link, posts on socials. Money trickles in over weeks. Treatment is partially funded by the community. The shame of asking is real. So is the gratitude. This is the parallel insurance system that exists in Kenya because the formal one is underused.',
            tone: 'bad',
          };
        },
      },
      {
        label: 'Take a SACCO/bank loan to fund treatment',
        meta: 'Debt now, repayment for years',
        apply: (s) => {
          s.claimEventOccurred = true;
          s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
          accrueMMFDirect(s, 5);
          s.claimOutcome = wasSurrendered ? 'no_cover_surrendered' : 'no_cover_lapsed';
          s.claimAmount = 0;
          return {
            story:
              "You take a 3M loan over 5 years. Treatment funded. Now you have a recurring monthly debt that will follow you well past recovery. The insurance premium you stopped paying was lower than the loan EMI you now have to service.",
            tone: 'bad',
          };
        },
      },
    ];
  }

  return [
    {
      label: 'File a claim',
      meta: 'Outcome depends entirely on earlier choices',
      apply: (s) => {
        s.claimEventOccurred = true;
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);

        if (s.claimEventVariant === 'disability' && !s.riderDisability) {
          s.claimOutcome = 'no_rider';
          s.claimAmount = 0;
          return {
            story:
              "<strong>No payout — you didn't buy a Disability rider.</strong> Your life policy pays only on death. The disability won't trigger payout. You'll need to liquidate your assets and rebuild.",
            tone: 'bad',
          };
        }
        if (s.claimEventVariant === 'critical_illness' && !s.riderCritical) {
          s.claimOutcome = 'no_rider';
          s.claimAmount = 0;
          return {
            story:
              "<strong>No payout — you didn't buy a Critical Illness rider.</strong> Your life policy will pay out when you die. It doesn't pay anything for being diagnosed. The treatment is on you.",
            tone: 'bad',
          };
        }
        if (s.health !== 'clean' && s.discCondition === 'hide') {
          s.claimOutcome = 'denied_nondisclosure';
          s.claimAmount = 0;
          return {
            story: `<strong>Claim investigation reveals non-disclosure.</strong> The insurer pulls 20 years of medical records. They find the ${s.health === 'chronic' ? 'chronic' : 'pre-existing'} condition you didn't declare at application. Letter from claims dept: "Per Section 8(2) of the policy schedule and Section 153 of the Insurance Act, the policy is voided ab initio for material non-disclosure. Premiums of KSh ${fmt(s.totalPremiumsPaid)} are being refunded. No claim payable." You're staring at the letter. You hid one fact 18 years ago.`,
            tone: 'bad',
          };
        }
        if (
          (s.claimEventVariant === 'disability' &&
            s.occupation === 'driver_rider' &&
            s.discHazard === 'hide') ||
          (s.claimEventVariant === 'disability' &&
            s.occupation === 'hazardous' &&
            s.discHazard === 'hide')
        ) {
          s.claimOutcome = 'denied_nondisclosure';
          s.claimAmount = 0;
          return {
            story:
              "<strong>Claim denied — undisclosed hazardous occupation.</strong> The investigators establish that the accident is directly related to the occupational activity you didn't declare. Premiums refunded. No claim payment.",
            tone: 'bad',
          };
        }
        s.claimOutcome = 'paid';
        s.claimAmount = Math.round(s.sumAssured * 0.5);
        return {
          story: `<strong>Claim approved.</strong> The ${s.claimEventVariant === 'disability' ? 'disability rider' : 'CI rider'} pays you 50% of sum assured — KSh ${fmt(s.claimAmount)}. Cheque arrives in 21 days. Recovery and treatment begin properly funded. <strong>This is what insurance is for.</strong>`,
          tone: 'good',
        };
      },
    },
    {
      label: "Don't file — assume you're not covered",
      meta: 'Many Kenyans do this out of fear or confusion',
      apply: (s) => {
        s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
        accrueMMFDirect(s, 5);
        s.claimEventOccurred = true;
        s.claimOutcome = 'didnt_file';
        return {
          story:
            "You don't file. <strong>Major mistake.</strong> If you had the right rider and disclosed honestly, you'd have been paid. The bill is on you. The insurance is still in force but irrelevant to today's emergency.",
          tone: 'bad',
        };
      },
    },
  ];
}

// direct mutating MMF helper (used inside choice apply functions that receive a mutable copy)
function accrueMMFDirect(s, years) {
  if (s.product !== 'term_invest') return;
  const termP = premiumPer1M(s.age, 'term') * (s.sumAssured / 1000000);
  const wholeP = premiumPer1M(s.age, 'whole') * (s.sumAssured / 1000000);
  const monthlyContrib = wholeP - termP;
  const monthlyRate = 0.1 / 12;
  for (let yr = 0; yr < years; yr++) {
    for (let mo = 0; mo < 12; mo++) {
      s.mmfBalance = s.mmfBalance * (1 + monthlyRate) + monthlyContrib;
    }
  }
  s.mmfBalance = Math.round(s.mmfBalance);
}

// ============================================================
// EVENT POOLS
// ============================================================
export const EVENT_POOLS = {
  3: [
    {
      condition: (s) =>
        s.maritalStatus === 'married' &&
        (s.spouseIncome === 0 || s.spouseIncome === null) &&
        s.children >= 1,
      title: 'Year 3 — Sole-earner squeeze',
      setup: (s) =>
        `It's been three years. You're now ${s.age + 3}. Your company restructured and your income dropped 30% temporarily. With ${s.children} ${s.children === 1 ? 'child' : 'children'} at home and your spouse not working, the monthly premium of KSh ${fmt(s.monthlyPremium)} suddenly feels heavy. School fees are due next month.`,
      choices: incomeShockChoices,
    },
    {
      condition: (s) => s.occupation === 'self_employed',
      title: 'Year 3 — Business turbulence',
      setup: (s) =>
        `Three years in. You're ${s.age + 3}. Your business had a brutal Q3 — a big client delayed payment for four months. The monthly premium of KSh ${fmt(s.monthlyPremium)} is now competing with payroll for your two employees.`,
      choices: incomeShockChoices,
    },
    {
      condition: () => true,
      title: 'Year 3 — Income shock',
      setup: (s) =>
        `It's been three years. You're now ${s.age + 3}. The company you work for has been restructuring. Your income has dropped 30% temporarily — you're picking up consultancy work but it's irregular. The monthly premium of KSh ${fmt(s.monthlyPremium)} suddenly feels heavy.`,
      choices: incomeShockChoices,
    },
  ],
  8: [
    {
      condition: (s) =>
        s.occupation === 'driver_rider' ||
        s.occupation === 'hazardous' ||
        s.occupation === 'field',
      title: 'Year 8 — A close call',
      setup: (s) => {
        const ageThen = s.age + 8;
        const desc =
          s.occupation === 'driver_rider'
            ? 'You had a minor motorcycle accident on Mombasa Road — no injuries, but the boda is totaled.'
            : s.occupation === 'hazardous'
            ? 'A near-miss at the construction site — a beam fell about 3 metres from where you were standing.'
            : 'You were on a field trip in a high-risk area and had to be airlifted back to Nairobi after the road closed.';
        return `You're ${ageThen}. ${desc} You're fine. But you start thinking about your insurance for the first time since taking it out.`;
      },
      choices: hazardCloseCallChoices,
    },
    {
      condition: (s) =>
        (s.dependants === 'parents' ||
          s.dependants === 'parents_siblings' ||
          s.dependants === 'extended') &&
        s.age + 8 >= 32,
      title: 'Year 8 — Parent hospitalized',
      setup: (s) =>
        `You're ${s.age + 8}. Your mother (or father) is admitted for what turns out to be hypertensive crisis. Bill at the hospital so far: KSh 180,000 and rising. Family is "looking up to you." This isn't your insurance — but it's draining the savings buffer that lets you keep paying your insurance premiums.`,
      choices: parentHospitalChoices,
    },
    {
      condition: (s) => s.children >= 1 && s.age + 8 >= 28,
      title: 'Year 8 — Child medical scare',
      setup: (s) =>
        `You're ${s.age + 8}. Your ${s.children === 1 ? 'child' : 'oldest child'} is rushed to hospital with a serious infection. They're going to be fine but the bill is KSh 240,000. You realize you don't have medical insurance for the children — only your life cover. Your savings take a serious hit.`,
      choices: kidMedicalChoices,
    },
    {
      condition: (s) =>
        s.maritalStatus === 'single' && s.age + 8 < 35 && s.children === 0,
      title: 'Year 8 — Career pivot',
      setup: (s) =>
        `You're ${s.age + 8}. You're offered a senior role in Dubai — 2.5× your current salary, 2-year contract. But it means relocation and your Kenyan policy needs careful review (is it portable? what about premium payments in foreign currency?). Some policies lapse if premiums come from abroad without notice.`,
      choices: careerPivotChoices,
    },
    {
      condition: () => true,
      title: 'Year 8 — A close family friend died',
      setup: (s) =>
        `You're ${s.age + 8}. A close family friend died unexpectedly — heart attack at 39. His family is struggling because his policy was only KSh 1M and he had three young children. You sit with the realization that you don't actually know if your own coverage is enough.`,
      choices: friendDeathChoices,
    },
  ],
  13: [
    {
      condition: (s) => s.health !== 'clean' && s.discCondition === 'hide',
      title: 'Year 13 — Medical records request',
      setup: (s) =>
        `You're ${s.age + 13}. The insurer sends a routine policy review questionnaire. It includes a clause: "we may pull medical records as part of periodic review." Your ${s.health === 'chronic' ? 'chronic' : 'pre-existing'} condition has been on file at the hospital since before your policy started. The records will show it.`,
      choices: medicalRecordsChoices,
    },
    {
      condition: (s) =>
        s.maritalStatus === 'married' && s.children === 0 && s.age + 13 < 40,
      title: 'Year 13 — Family planning',
      setup: (s) =>
        `You're ${s.age + 13}. You and your spouse are starting to plan a family. The conversations now include "what if something happens to one of us during the pregnancy or first years?" You realize your current cover is just on you, not your spouse.`,
      choices: familyPlanningChoices,
    },
    {
      condition: (s) => s.children >= 2 && s.age + 13 >= 38,
      title: 'Year 13 — Three school fees at once',
      setup: (s) =>
        `You're ${s.age + 13}. Two children in secondary school, one in primary. Term fees are KSh 320,000. Your insurance premium of KSh ${fmt(s.monthlyPremium)}/month suddenly competes directly with school fees. You're asking the hard question: which is more important right now?`,
      choices: schoolFeesCrunchChoices,
    },
    {
      condition: (s) => s.age + 13 >= 50,
      title: 'Year 13 — Diagnosis',
      setup: (s) =>
        `You're ${s.age + 13}. Routine check-up flags Type 2 diabetes. Manageable with lifestyle changes and medication. But it's on your medical record now and you start thinking carefully about your insurance for the first time in years.`,
      choices: ownHealthDiagnosisChoices,
    },
    {
      condition: () => true,
      title: 'Year 13 — Medical update',
      setup: (s) =>
        `You're ${s.age + 13}. Routine medical check-up flags hypertension. Stage 1. You're given lifestyle advice and a low-dose medication. Nothing dramatic — but it's on your medical record now. You start wondering about your insurance.`,
      choices: ownHealthDiagnosisChoices,
    },
  ],
  18: [
    {
      condition: (s) =>
        s.occupation === 'driver_rider' || s.occupation === 'hazardous',
      title: 'Year 18 — Accident',
      setup: (s) =>
        `You're ${s.age + 18}. ${s.occupation === 'driver_rider' ? 'A serious accident on the road. You survive but your left leg is amputated below the knee.' : 'A workplace incident — heavy machinery accident. You survive but with permanent spinal damage. You will not work again in this field.'} Medical bills are KSh 1.8M. Future earning capacity reduced by 60%.`,
      claimType: 'disability',
      choices: claimEventChoices,
    },
    {
      condition: () => true,
      title: 'Year 18 — Diagnosis',
      setup: (s) => {
        const ageThen = s.age + 18;
        return `You're ${ageThen}. After weeks of feeling unwell, the oncologist confirms it: ${ageThen < 50 ? 'an aggressive lymphoma' : 'colon cancer, Stage 2'}. Treatable but expensive. Hospital quotes the full treatment plan at KSh 3.4 million. You're staring at the wall.<br><br>Time to find out what your insurance actually does.`;
      },
      claimType: 'critical_illness',
      choices: claimEventChoices,
    },
  ],
  23: [
    {
      condition: () => true,
      title: 'Year 23 — The counting',
      setup: (s) =>
        `You're ${s.age + 23}. ${s.claimOutcome === 'paid' || s.claimOutcome === 'paid_via_ira' ? 'You survived.' : s.claimEventOccurred ? 'You survived, somehow.' : 'Life has continued.'}<br><br>Twenty-three years of paying premiums (or trying to). Time to look at what your insurance has actually done versus what other strategies would have done with the same money.`,
      choices: (s) => [
        {
          label: 'Run the comparison',
          meta: 'See your numbers vs the alternatives',
          apply: (s) => {
            s.totalPremiumsPaid += s.monthlyPremium * 12 * 5;
            accrueMMFDirect(s, 5);
            if (!s.policyLapsed) {
              if (s.product === 'whole')
                s.cashValue = Math.round(s.totalPremiumsPaid * 0.72);
              if (s.product === 'endowment')
                s.cashValue = Math.round(s.totalPremiumsPaid * 1.15);
            }
            return {
              story:
                'The numbers are in. See your full breakdown on the next screen.',
              tone: 'good',
            };
          },
        },
      ],
    },
  ],
};

export function getActiveEvent(yearOffset, state) {
  const pool = EVENT_POOLS[yearOffset];
  for (const v of pool) {
    if (v.condition(state)) {
      return { yearOffset, ...v };
    }
  }
  return { yearOffset, ...pool[pool.length - 1] };
}

// ============================================================
// INSIGHTS GENERATOR
// ============================================================
export function generateInsights(state) {
  const out = [];
  const s = state;

  if (s.agentChoice === 'agreed') {
    out.push({
      tone: 'bad',
      headline: 'You were missold by the agent',
      body: "You went into the meeting wanting one product and left with another. The agent's commission was higher, your premium was higher, and the fit with your life was worse. This is the single biggest pattern in Kenyan life insurance complaints — agents pushing whole life and endowment for products that pay them more, regardless of whether the customer needs them.",
    });
  } else if (
    s.agentChoice === 'pushed_back' ||
    s.agentChoice === 'asked_questions' ||
    s.agentChoice === 'thought_more'
  ) {
    out.push({
      tone: 'good',
      headline: 'You pushed back on the agent',
      body: "Asking one specific question — \"what are the actual numbers?\" or \"what's your commission?\" — is enough to shut down most misselling attempts. The agent's power comes from your willingness to defer. The moment you ask for data, the dynamic changes.",
    });
  }

  if (s.health !== 'clean' && s.discCondition === 'hide') {
    out.push({
      tone: 'bad',
      headline: 'You hid your pre-existing condition',
      body: `You had a ${s.health === 'chronic' ? 'chronic' : 'minor'} condition and didn't declare it. ${s.claimOutcome === 'denied_nondisclosure' ? 'It cost you the claim payout when you needed it most.' : 'You got away with it this round. In real life, insurers investigate claims thoroughly and 18+ years of medical records are accessible to them.'} Disclosing honestly costs you 15-40% in premium loading. Hiding it costs you the whole claim.`,
    });
  }

  if (
    s.occupation === 'driver_rider' ||
    s.occupation === 'hazardous' ||
    s.occupation === 'field'
  ) {
    if (s.discHazard === 'hide') {
      out.push({
        tone: 'bad',
        headline: 'You hid your hazardous occupation',
        body: "Bodaboda, construction, frequent travel to high-risk areas — these all materially affect mortality risk. Hiding them at application means any death related to that activity is excluded. And insurers investigate. Phone records, witness statements, social media — your ride patterns are visible.",
      });
    } else if (s.discHazard === 'honest') {
      out.push({
        tone: 'good',
        headline: 'You disclosed the occupational risk',
        body: 'You paid a 35% premium loading. You also bought yourself the right to actually be paid out if the worst happens. That is the trade. Worth it.',
      });
    }
  }

  if (s.readExclusions === false) {
    out.push({
      tone: 'bad',
      headline: "You didn't read the exclusions",
      body: "You signed without reading the 47-page document. Most people do. The exclusions that were in your policy and you didn't see: 24-month suicide clause, intoxication exclusion, war/terrorism exclusion, aviation exclusion, 6-month critical illness waiting period. Each of these is a real reason for real claim denials in Kenya every year. The 5 minutes to skim them is the cheapest insurance you can buy.",
    });
  } else if (s.readExclusions === true) {
    out.push({
      tone: 'good',
      headline: 'You read the policy exclusions',
      body: "You're now in the small minority of policyholders who actually know what their policy will and won't pay for. This single act gives you a massive advantage at claim time — you know what to argue and what not to.",
    });
  }

  if (s.amlOutcome === 'rejected') {
    out.push({
      tone: 'bad',
      headline: 'Your application was rejected by compliance',
      body: "Refusing KYC and source-of-funds questions ended your application before it started. POCAMLA isn't optional — every regulated Kenyan insurer must do this. Refusing doesn't mean privacy; it means no insurance. You went through 25 years uninsured because of one decision at the application desk.",
    });
  } else if (s.amlOutcome === 'delayed') {
    out.push({
      tone: 'neutral',
      headline: 'Your application went through Enhanced Due Diligence',
      body: "The 6-week delay and 12% premium loading were the cost of giving the compliance team extra work. KYC frictions are real but they're also why criminals can't easily launder money through insurance. The system worked as intended.",
    });
  } else if (
    s.amlIDProvided === 'id_payslip' &&
    (s.discSource === 'salary' || s.discSource === 'business')
  ) {
    out.push({
      tone: 'good',
      headline: 'You cooperated with KYC',
      body: 'Providing full ID, payslip, KRA PIN, and a clear source of funds is the right move. It speeds up underwriting, prevents future disputes, and protects you if anyone later questions where your premium money came from.',
    });
  }

  if (s.policyLapsed && !s.boughtNewPolicy) {
    out.push({
      tone: 'bad',
      headline: "Your policy lapsed and you didn't replace it",
      body: "Once a policy lapses, you're uninsured. The most common cause of life-insurance policy lapse in Kenya is income shock — which is also when life insurance matters most. The trick is to size your premium so it fits even in bad months, not just good ones.",
    });
  }

  if (s.surrenderedAmount > 0) {
    out.push({
      tone: 'bad',
      headline: 'You surrendered the policy',
      body: `You got back KSh ${fmt(s.surrenderedAmount)} — about ${Math.round((s.surrenderedAmount / s.totalPremiumsPaid) * 100)}% of what you'd paid in. The other ${Math.round((1 - s.surrenderedAmount / s.totalPremiumsPaid) * 100)}% (KSh ${fmt(s.totalPremiumsPaid - s.surrenderedAmount)}) was forfeited. This is one of the worst financial decisions in insurance — the loadings are heaviest in early years and surrender values are correspondingly low.`,
    });
  }

  if (s.boughtNewPolicy) {
    out.push({
      tone: 'neutral',
      headline: 'You bought a new policy after lapse',
      body: `Your replacement policy at age ${s.newPolicyAge} costs KSh ${fmt(s.newPolicyPremium)}/month — significantly higher than what you'd be paying if you'd kept the original. The lifetime cost of letting a policy lapse is the difference between original and replacement premium, compounded over the remaining years.`,
    });
  }

  if (s.product === 'term_invest') {
    out.push({
      tone: 'good',
      headline: 'You bought term and invested the difference',
      body: `You paid the lowest premium for protection AND built a separate portfolio that's now worth roughly KSh ${fmt(s.mmfBalance)}. This is the strategy most fee-only financial advisors recommend for a reason. It separates the two financial functions (protection vs wealth) so each can be optimized independently.`,
    });
  }

  if (s.claimOutcome === 'paid') {
    out.push({
      tone: 'good',
      headline: 'Your claim was paid',
      body: 'The whole system worked. You disclosed honestly, kept the policy in force, had the right rider, and when the worst happened the insurer paid. This is the rare-but-real outcome that makes the years of premiums worth it.',
    });
  } else if (s.claimOutcome === 'paid_via_ira') {
    out.push({
      tone: 'ira',
      headline: 'You won at IRA',
      body: "The insurer denied you. You didn't accept it. You filed a complaint with IRA. After investigation, they ordered the insurer to pay. This is the safety net most policyholders never use. Free, available, and effective when the case has merit. complaints@ira.go.ke.",
    });
  } else if (s.claimOutcome === 'no_rider') {
    out.push({
      tone: 'bad',
      headline: "You didn't have the right rider",
      body: "A life insurance policy pays when you die. A Critical Illness rider pays when you're diagnosed with a covered condition while alive. A Disability rider pays when you become permanently disabled. They cost different and pay differently. Most Kenyans skip these riders to save 1-2K/month, then face medical bills the policy was never designed to cover.",
    });
  }

  if (s.iraComplaintFiled) {
    out.push({
      tone: 'ira',
      headline: 'You used the IRA Consumer Protection pathway',
      body: "Whether you won or lost, you exercised your right as a policyholder. Most Kenyans accept the first denial as final. The fact that you didn't means future generations of policyholders also win when their insurers see that complaints are taken seriously.",
    });
  }

  if (out.length === 0) {
    out.push({
      tone: 'neutral',
      headline: 'A neutral run',
      body: "No big lessons triggered. Replay with different choices — hide a disclosure, change agents, take a different product — and see how the same engine plays out very differently.",
    });
  }

  return out;
}